import { useState } from "react";
import { CodeStatus, Location, PermitType, SignatureType } from "../common/enums";
import {
    DivingPermitFormState_default,
    DivingPermitFormState_populateErrorMessages,
    DivingPermitFormState_toRequest
} from "../types/forms/Permits/DivingPermitFormState";
import { PermitFormProps } from "../types/forms/Permits/PermitFormProps";
import { DivingPermit_toFormState } from "../types/permits/DivingPermit";
import { Signature_empty } from "../types/Signature";
import usePortControlPermitForm from "./usePortControlPermitForm";
import useSignature from "./useSignature";
import { Blob_toBase64Async } from "../common/blobUtils";
import useSearchDropdown from "./useSearchDropdown";
import { searchWorkPermitsForConnection, updatePermitParticipantsAsync } from "../apiClient";
import UpdateParticipantsRequest from "../types/requests/UpdateParticipantsRequest";
import ParticipantsList from "../components/Forms/Permits/ParticipantsList";
import { showDangerDialog, showSuccessAlert } from "../common";

function useDivingPermitForm(props: PermitFormProps) {
    //
    // This is only used for new permits where the signature is not stored until the permit is created.
    //
    const [apmtDraftSignature, setAPMTDraftSignature] = useState(
        Signature_empty(SignatureType.APMTInformationConfirmation, true, true)
    );

    const baseformManager = usePortControlPermitForm({
        ...props,
        defaultFormState: DivingPermitFormState_default,
        permitType: PermitType.DivingPermit,
        populateErrorMessages: DivingPermitFormState_populateErrorMessages,
        toFormState: DivingPermit_toFormState,
        toRequest: DivingPermitFormState_toRequest,
        clearErrorMessages: (formState) => DivingPermitFormState_populateErrorMessages(formState, {})
    });

    const { storedPermit } = baseformManager;

    const apmtSignature = !!baseformManager.storedPermit
        ? baseformManager.storedPermit.apmtInformationConfirmationSignature
        : apmtDraftSignature;

    const apmtSignatureManager = useSignature({
        signature: apmtSignature,
        baseUserInfo: props.baseUserInfo,
        resources: props.resources,
        onRemoveSignature: removeAPMTSignatureManager,
        onSaveSignature: saveAPMTSignatureManagerAsync
    });

    const energyHarbourPermitSearchHandler = useSearchDropdown({
        performSearch: async (input) => searchWorkPermitsForConnection(input, PermitType.EnergyHarbourPermit),
        searchErrorText: props.resources.get("SearchError") ?? "",
        toSearchResult: (wp) => {
            const positions = wp.positions
                .filter((pos) => pos.comment)
                .map((pos) => pos.comment)
                .join(", ");

            const searchResultText = `${wp.sequenceIdentifier}, ${wp.status}, ${positions}, ${wp.companyName}, ${wp.description}`;

            return { id: searchResultText, text: searchResultText, result: wp };
        },
        onSearchResultClick: (r) => {
            baseformManager.setFormState((prevValue) => ({
                ...prevValue,
                energyHarbourSequenceIdentifier: {
                    ...prevValue.energyHarbourSequenceIdentifier,
                    value: r.result.sequenceIdentifier
                }
            }));
        }
    });

    function removeAPMTSignatureManager() {
        baseformManager.setFormState((prevValue) => ({
            ...prevValue,
            apmtInformationConfirmationSignature: { ...prevValue.apmtInformationConfirmationSignature, value: "" },
            apmtSignatureNameClarification: { ...prevValue.apmtSignatureNameClarification, value: "" }
        }));

        setAPMTDraftSignature(Signature_empty(SignatureType.APMTInformationConfirmation, true, true));

        return Promise.resolve();
    }

    async function saveAPMTSignatureManagerAsync(signatureBlob: Blob, nameClarification: string) {
        const base64Signature = await Blob_toBase64Async(signatureBlob);

        baseformManager.setFormState((prevValue) => ({
            ...prevValue,
            apmtInformationConfirmationSignature: {
                ...prevValue.apmtInformationConfirmationSignature,
                value: base64Signature
            },
            apmtSignatureNameClarification: { ...prevValue.apmtSignatureNameClarification, value: nameClarification }
        }));

        setAPMTDraftSignature((prevValue) => ({ ...prevValue, base64Signature, nameClarification }));
    }

    async function updateParticipantsButtonClickHandlerAsync() {
        if (!storedPermit) {
            console.error("Tried to update the participants with no stored permit.");
            return;
        }

        const request: UpdateParticipantsRequest = {
            projectLeaderIsActive: false,
            workLeaderIsActive: false,
            participants: baseformManager.formState.persons.map((p) => ({
                isActive: p.isActive,
                name: p.name,
                phoneNumber: p.phoneNumber
            }))
        };

        const updateResult = await updatePermitParticipantsAsync(
            storedPermit.entityId,
            request,
            PermitType.DivingPermit
        );

        if (updateResult.failed) {
            if (!updateResult.error.errors) {
                showDangerDialog(
                    props.resources.get("SomethingWentWrong"),
                    props.resources.get("RefreshPageAndTryAgain")
                );
                return;
            }

            baseformManager.handleValidationErrors(updateResult.error);
            return;
        }

        showSuccessAlert(props.resources.get("ParticipantsUpdated"));
        await baseformManager.refreshStoredPermitAsync();
    }

    const showAPMTConditions = baseformManager.formState.divingLocation.value === Location.APMT;
    const showEnergyHarbourConditions = baseformManager.formState.divingLocation.value === Location.EnergyHarbour;

    const canManageParticipants = !storedPermit || storedPermit.canManageParticipants;

    const showUpdateParticipantsButton = !!storedPermit && !storedPermit.canUpdate && canManageParticipants;

    const showPrefillEnergyHarbourPermitButton = !!storedPermit && storedPermit.canPrefillEnergyHarbourPermit;

    return {
        ...baseformManager,
        showAPMTConditions,
        showEnergyHarbourConditions,
        apmtSignatureManager,
        energyHarbourPermitSearchHandler,
        showUpdateParticipantsButton,
        showPrefillEnergyHarbourPermitButton,
        canManageParticipants,
        updateParticipantsButtonClickHandlerAsync
    };
}

export default useDivingPermitForm;
